import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { societyInfo } from '../data/mock';
import { Mail, Phone, MapPin, Facebook, Instagram } from 'lucide-react';

const Footer = () => {
  const { t, language } = useLanguage();
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { href: '#about', label: t.nav.about },
    { href: '#history', label: t.nav.history },
    { href: '#area', label: t.nav.area },
    { href: '#gallery', label: t.nav.gallery },
    { href: '#news', label: t.nav.news },
    { href: '#contact', label: t.nav.contact },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-stone-950 border-t border-stone-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer */}
        <div className="py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <a href="#" className="flex items-center gap-3 mb-6">
              <div className="w-16 h-16 rounded-full flex items-center justify-center overflow-hidden bg-white shadow-lg">
                <img 
                  src={societyInfo.logo} 
                  alt="LD Rakek Logo" 
                  className="w-14 h-14 object-contain"
                />
              </div>
              <div>
                <span className="block text-lg font-bold text-white">
                  {language === 'sl' ? societyInfo.name : societyInfo.nameEn}
                </span>
                <span className="block text-xs text-emerald-500 tracking-wider uppercase font-semibold">
                  {language === 'sl' ? 'Od leta' : 'Since'} {societyInfo.established}
                </span>
              </div>
            </a>
            <p className="text-stone-400 mb-6 max-w-md">
              {language === 'sl' ? societyInfo.description : societyInfo.descriptionEn}
            </p>
            <div className="flex items-center gap-3">
              <a
                href="#"
                className="w-10 h-10 bg-stone-800 hover:bg-emerald-600 rounded-lg flex items-center justify-center text-stone-400 hover:text-white transition-all duration-200"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-stone-800 hover:bg-emerald-600 rounded-lg flex items-center justify-center text-stone-400 hover:text-white transition-all duration-200"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-6">
              {language === 'sl' ? 'Hitre povezave' : 'Quick Links'}
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-stone-400 hover:text-emerald-400 transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold mb-6">{t.contact.title}</h3>
            <ul className="space-y-4">
              <li>
                <a
                  href={`mailto:${societyInfo.email}`}
                  className="flex items-center gap-3 text-stone-400 hover:text-emerald-400 transition-colors"
                >
                  <Mail className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{societyInfo.email}</span>
                </a>
              </li>
              <li>
                <a
                  href={`tel:${societyInfo.phone}`}
                  className="flex items-center gap-3 text-stone-400 hover:text-emerald-400 transition-colors"
                >
                  <Phone className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{societyInfo.phone}</span>
                </a>
              </li>
              <li>
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(societyInfo.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-stone-400 hover:text-emerald-400 transition-colors"
                >
                  <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{societyInfo.address}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-stone-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-stone-500 text-sm">
            &copy; {currentYear} {language === 'sl' ? societyInfo.name : societyInfo.nameEn}. {t.footer.rights}
          </p>
          <div className="flex items-center gap-6 text-sm">
            <a href="#" className="text-stone-500 hover:text-stone-300 transition-colors">
              {t.footer.privacy}
            </a>
            <a href="#" className="text-stone-500 hover:text-stone-300 transition-colors">
              {t.footer.terms}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
